import { useState } from 'react';
import { AlertTriangle, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@easyflow/ui-master/components/ui/dialog';
import { Button } from '@easyflow/ui-master/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import api from '@/lib/api';
import type { TerminateBackendRequest, TerminateBackendResponse } from '@easyflow/shared';

interface Props {
  target: { pid: number; mode: TerminateBackendRequest['mode'] } | null;
  onClose: () => void;
  onSuccess: () => void;
}

export function TerminateBackendDialog({ target, onClose, onSuccess }: Props) {
  const { toast } = useToast();
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!target) return;
    setBusy(true);
    try {
      const body: TerminateBackendRequest = { pid: target.pid, mode: target.mode, confirm: true };
      const res = await api.post<TerminateBackendResponse>('/master/database/terminate-backend', body);
      if (res.data?.ok) {
        toast({ title: 'Eseguito', description: res.data.message });
        onSuccess();
      } else {
        toast({
          title: 'Operazione non riuscita',
          description: res.data?.message ?? 'pid non terminabile',
          variant: 'destructive',
        });
        setBusy(false);
      }
    } catch (err) {
      const e = err as { response?: { data?: { error?: string } }; message?: string };
      toast({
        title: 'Errore',
        description: e.response?.data?.error ?? e.message ?? 'Errore di rete',
        variant: 'destructive',
      });
      setBusy(false);
    }
  };

  const open = target !== null;
  const isCancel = target?.mode === 'cancel';

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o && !busy) onClose();
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-rose-600">
            <AlertTriangle className="h-5 w-5" />
            {isCancel ? 'Cancel query backend' : 'Terminate backend'}
          </DialogTitle>
          <DialogDescription>
            {isCancel
              ? `Verrà inviato pg_cancel_backend(${target?.pid}) per annullare la query in corso. La connessione rimane aperta.`
              : `Verrà inviato pg_terminate_backend(${target?.pid}) per chiudere forzatamente la connessione. Eventuali transazioni in corso vengono rollback-ate.`}
          </DialogDescription>
        </DialogHeader>
        <div className="text-xs text-muted-foreground bg-amber-50 dark:bg-amber-950/30 border border-amber-200 rounded p-2">
          Azione tracciata in audit log. Conferma esplicita richiesta.
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Annulla</Button>
          <Button variant="destructive" onClick={() => void submit()} disabled={busy}>
            {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Conferma {isCancel ? 'cancel' : 'terminate'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
